import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  /**
   * Server-side environment variables (not exposed to the browser).
   * These come from wrangler.toml [vars] at runtime, and from .env.local during local dev.
   * Marked optional because they're unavailable during Workers Builds (build-time).
   */
  server: {
    LANDING_DEMO_ORG_ID: z.uuid().optional(),
    LANDING_DEMO_ORG_USER_ID: z.uuid().optional(),
    POSTHOG_HOST: z.url().optional().default("https://eu.i.posthog.com"),
    POSTHOG_API_KEY: z.string().optional(),
    NHOST_ADMIN_SECRET: z.string().min(1).optional(),
    NHOST_JWT_SECRET: z.string().min(1).optional(),
    NHOST_WEBHOOK_SECRET: z.string().min(1).optional(),
    SERVER_WEBHOOK_SECRET: z.string().min(1).optional(),
    STRIPE_SECRET_KEY: z.string().optional(),
    OPENAI_API_KEY: z.string().optional(),
    GROQ_API_KEY: z.string().optional(),
    YTDLP_API_KEY: z.string().optional().default(""),
    YOUTUBE_INFO_URL: z.url().optional(),
    VIPPS_CLIENT_ID: z.string().optional(),
    VIPPS_CLIENT_SECRET: z.string().optional(),
    VIPPS_MERCHANT_SERIAL_NUMBER: z.string().optional(),
    VIPPS_OCM_SUBSCRIPTION_KEY: z.string().optional(),
  },

  /**
   * Client-side environment variables (exposed to the browser).
   * These come from .env.production or .env.development and are inlined at build time.
   * All are required — a missing var means the .env file is incomplete.
   */
  client: {
    NEXT_PUBLIC_API_URL: z.url(),
    NEXT_PUBLIC_NHOST_AUTH_URL: z.url(),
    NEXT_PUBLIC_NHOST_FUNCTIONS_URL: z.url(),
    NEXT_PUBLIC_NHOST_GQL_URL: z.url(),
    NEXT_PUBLIC_NHOST_STORAGE_URL: z.url(),
    NEXT_PUBLIC_FRONTEND_URL: z.url(),
    NEXT_PUBLIC_BLOG_URL: z.url(),
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().startsWith("pk_"),
    NEXT_PUBLIC_STRIPE_1_HOUR_CREDITS_PRICE_ID: z.string().startsWith("price_"),
    NEXT_PUBLIC_RESULT_BUCKET_NAME: z.string().min(1),
    NEXT_PUBLIC_RESULT_BUCKET_R2: z.url(),
    NEXT_PUBLIC_GTM_ID: z.union([z.string().startsWith("GTM-"), z.literal("")]),
    NEXT_PUBLIC_MICROSOFT_CLARITY_ID: z.string(),
    // Empty string = analytics disabled (e2e tests); layouts/init are guarded.
    NEXT_PUBLIC_POSTHOG_KEY: z.union([z.string().startsWith("phc_"), z.literal("")]),
    NEXT_PUBLIC_SENTRY_DSN: z.union([z.url(), z.literal("")]),
    NEXT_PUBLIC_SENTRY_ENV: z.enum(["local", "development", "production"]),
    NEXT_PUBLIC_SENTRY_RELEASE: z.string().optional(),
    NEXT_PUBLIC_ENABLE_EXPERIMENTAL_MEDIA_TOOLS: z.string().optional(),
    // Dev trial: cue translation is unbilled until TODO(billing) in the API
    // lands, so it needs its own opt-in on top of the media-tools flag.
    NEXT_PUBLIC_ENABLE_CUE_TRANSLATION: z.string().optional(),
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: z.string().min(1),
  },

  /**
   * Next.js >= 13.4.4: only client-side vars need explicit destructuring.
   * Server-side vars are read from process.env at runtime automatically.
   */
  experimental__runtimeEnv: {
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_NHOST_AUTH_URL: process.env.NEXT_PUBLIC_NHOST_AUTH_URL,
    NEXT_PUBLIC_NHOST_FUNCTIONS_URL: process.env.NEXT_PUBLIC_NHOST_FUNCTIONS_URL,
    NEXT_PUBLIC_NHOST_GQL_URL: process.env.NEXT_PUBLIC_NHOST_GQL_URL,
    NEXT_PUBLIC_NHOST_STORAGE_URL: process.env.NEXT_PUBLIC_NHOST_STORAGE_URL,
    NEXT_PUBLIC_FRONTEND_URL: process.env.NEXT_PUBLIC_FRONTEND_URL,
    NEXT_PUBLIC_BLOG_URL: process.env.NEXT_PUBLIC_BLOG_URL,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    NEXT_PUBLIC_STRIPE_1_HOUR_CREDITS_PRICE_ID:
      process.env.NEXT_PUBLIC_STRIPE_1_HOUR_CREDITS_PRICE_ID,
    NEXT_PUBLIC_RESULT_BUCKET_NAME: process.env.NEXT_PUBLIC_RESULT_BUCKET_NAME,
    NEXT_PUBLIC_RESULT_BUCKET_R2: process.env.NEXT_PUBLIC_RESULT_BUCKET_R2,
    NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
    NEXT_PUBLIC_MICROSOFT_CLARITY_ID: process.env.NEXT_PUBLIC_MICROSOFT_CLARITY_ID,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    NEXT_PUBLIC_SENTRY_ENV: process.env.NEXT_PUBLIC_SENTRY_ENV,
    NEXT_PUBLIC_SENTRY_RELEASE: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
    NEXT_PUBLIC_ENABLE_EXPERIMENTAL_MEDIA_TOOLS:
      process.env.NEXT_PUBLIC_ENABLE_EXPERIMENTAL_MEDIA_TOOLS,
    NEXT_PUBLIC_ENABLE_CUE_TRANSLATION: process.env.NEXT_PUBLIC_ENABLE_CUE_TRANSLATION,
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
  },

  /**
   * Skip validation when SKIP_ENV_VALIDATION is set (useful for linting, typechecking).
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
});

/**
 * Assert that an optional server env var is present at runtime.
 * Server vars are marked optional in the schema because they're unavailable during
 * Cloudflare Workers builds, but are always set at runtime (wrangler.toml / .env.local).
 * Use this instead of `env.VAR!` to get a clear error message on misconfiguration.
 */
export function requireServerEnv<T>(value: T | undefined, name: string): T {
  if (value === undefined) {
    throw new Error(`Missing required server environment variable: ${name}`);
  }
  return value;
}
